import React, { useState } from "react";
import api, { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Search, FileDown, AlertTriangle } from "lucide-react";
import { fmtINR } from "@/lib/format";

/**
 * Batch Recall Trace — Iter-103
 * Enter a batch_no → shows every inward + outward movement + impacted invoices.
 * One-click PDF recall notice for customer notification.
 */
export default function BatchRecall() {
  const [batch, setBatch] = useState("");
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);

  const trace = async () => {
    if (!batch.trim()) return toast.error("Enter batch number");
    setLoading(true);
    try {
      const r = await api.get(`/reports/batch-recall?batch_no=${encodeURIComponent(batch.trim())}`);
      setD(r.data);
      if (!r.data.found) toast.warning(`Batch ${batch} not found`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setLoading(false);
  };

  const downloadPdf = () => {
    if (!d?.found) return toast.error("Trace a batch first");
    const url = `${API}/reports/batch-recall/pdf?batch_no=${encodeURIComponent(batch)}`;
    const a = document.createElement("a");
    a.href = url; a.target = "_blank"; a.rel = "noopener";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  return (
    <div className="space-y-4" data-testid="batch-recall">
      <div className="text-[10px] font-mono uppercase text-muted-foreground">// Batch recall trace · Iter-103</div>
      <Card className="rounded-sm p-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[220px]">
            <label className="text-[10px] uppercase font-mono tracking-widest text-muted-foreground">Batch #</label>
            <Input value={batch} onChange={(e) => setBatch(e.target.value)} placeholder="e.g. B240501-A"
                   className="rounded-sm mt-1 font-mono" data-testid="recall-batch-input"
                   onKeyDown={(e) => e.key === "Enter" && trace()} />
          </div>
          <Button size="sm" onClick={trace} disabled={loading} className="rounded-sm gap-1" data-testid="recall-trace-btn">
            <Search className="w-3.5 h-3.5" /> {loading ? "Tracing…" : "Trace"}
          </Button>
          {d?.found && (
            <Button size="sm" variant="outline" onClick={downloadPdf} className="rounded-sm gap-1" data-testid="recall-pdf-btn">
              <FileDown className="w-3.5 h-3.5" /> Recall Notice PDF
            </Button>
          )}
        </div>
      </Card>

      {d && d.found && (
        <>
          <div className="grid md:grid-cols-4 gap-3">
            <Card className="rounded-sm p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Inward moves</div><div className="text-2xl font-bold tabular-nums mt-1">{d.inward.length}</div></Card>
            <Card className="rounded-sm p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Outward moves</div><div className="text-2xl font-bold tabular-nums mt-1">{d.outward.length}</div></Card>
            <Card className="rounded-sm p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Impacted invoices</div><div className="text-2xl font-bold tabular-nums mt-1">{d.invoices.length}</div></Card>
            <Card className="rounded-sm p-3 border border-amber-500/40 bg-amber-500/10">
              <div className="text-[10px] font-mono uppercase text-amber-800 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Impacted customers</div>
              <div className="text-2xl font-bold tabular-nums mt-1 text-amber-800">{d.impacted_customers || 0}</div>
            </Card>
          </div>

          <Card className="rounded-sm p-3" data-testid="recall-invoices-table">
            <div className="text-[10px] font-mono uppercase text-muted-foreground mb-2">// Impacted invoices — send recall notice</div>
            <div className="overflow-auto">
              <table className="w-full dense">
                <thead><tr><th>Invoice #</th><th>Date</th><th>Customer</th><th className="text-right">Amount</th></tr></thead>
                <tbody>
                  {d.invoices.length === 0 && <tr><td colSpan="4" className="text-center py-6 text-muted-foreground">No invoices — batch not yet sold.</td></tr>}
                  {d.invoices.map((inv) => (
                    <tr key={inv.id} data-testid={`recall-inv-${inv.invoice_number}`}>
                      <td className="font-mono text-xs">{inv.invoice_number}</td>
                      <td className="text-xs">{inv.date}</td>
                      <td className="text-xs">{inv.customer_name || "—"}</td>
                      <td className="text-right tabular-nums">{fmtINR(inv.grand_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {d && !d.found && (
        <Card className="rounded-sm p-6 text-center text-sm text-muted-foreground" data-testid="recall-not-found">
          Batch <span className="font-mono">{batch}</span> not found in stock movements.
        </Card>
      )}
    </div>
  );
}
